<template>
  <k-responsive :aspect-ratio="16/9" class="ks-video-player">
    <slot v-if="!loadPlayer"></slot>
    <div class="start-container elevation-0" v-if="!started && loadPlayer && !unavailable" @click="play">
      <k-btn icon v-if="!checking"><k-icon x-large>play_arrow</k-icon></k-btn>
    </div>
    <div class="start-container unavailable-container elevation-0" v-if="!started && loadPlayer && unavailable">
      <div class="unavailable-content">
        <div class="unavailable-text">Video is not available</div>
        <k-btn icon @click="retry"><k-icon x-large>refresh</k-icon></k-btn>
      </div>
    </div>

    <div v-if="started" style="width: 100%; height: 100%; display: flex;" ref="player_container" class="player-container" :class="{'player-container-scaled': lufs || frameMonitor || latency}">
      <video style="width: 100%; height: 100%; padding:0px; object-fit: contain;" id="video" preload="metadata" playsinline ref="player" class="elevation-0" @click="play_toggle"></video>
      <canvas v-if="diamond" ref="diamond" class="player-overlay-canvas"></canvas>
      <canvas v-if="lufs && lufs_radar" ref="lufs_radar" class="lufs-radar"></canvas>
      <canvas v-if="audioMeters" ref="audio_meters" class="audio-meters"></canvas>
      <div v-if="buffering" class="buffering-indicator" :style="audioMeters && audio_meters_width ? {left: (audio_meters_width + 8) + 'px'} : null">
        <k-progress-circular indeterminate :size="24" :width="3" color="white"></k-progress-circular>
      </div>
      <!-- Status in the top right corner; the texts are written directly by the meters -->
      <div v-if="lufs || frameMonitor" ref="player_status" class="player-status">
        <!-- Freeze / black frame counts; click emits "frame-monitor-click" (or resets them without a listener) -->
        <div v-if="frameMonitor" ref="frame_item" class="player-status-item frame-display">
          <span ref="frame_display" class="player-status-text" @click.stop="frame_display_click"></span>
          <span v-if="$listeners['frame-monitor-close']" class="player-status-close" @click.stop="$emit('frame-monitor-close')"><k-icon inherit>close</k-icon></span>
        </div>
        <!-- Short-term loudness; click toggles the loudness radar -->
        <div v-if="lufs" class="player-status-item lufs-display">
          <span ref="lufs_display" class="player-status-text" @click.stop="toggle_lufs_radar"></span>
          <span v-if="$listeners['lufs-close']" class="player-status-close" @click.stop="$emit('lufs-close')"><k-icon inherit>close</k-icon></span>
        </div>
      </div>
      <!-- Bottom right corner, above the control bar: SMPTE timecode (only when the stream has one) and
           the latency from the MISB ST 0604 timestamps, shown right away with a spinner until the first value -->
      <div v-if="latency" class="player-bottom-status">
        <div class="player-status-item timecode-display">
          <span ref="timecode_display" class="player-status-text"></span>
        </div>
        <div class="player-status-item latency-display" v-tooltip.bottom="latency_info" @mouseenter="set_latency_hover(true)" @mouseleave="set_latency_hover(false)">
          <span class="latency-pending">Latency<k-progress-circular indeterminate :size="11" :width="2" color="white"></k-progress-circular></span>
          <span ref="latency_display" class="player-status-text"></span>
          <span v-if="$listeners['latency-close']" class="player-status-close" @click.stop="$emit('latency-close')"><k-icon inherit>close</k-icon></span>
        </div>
      </div>
      <!-- Seek bar -->
      <div 
        v-if="!is_live && duration > 0"
        class="seek-bar-container"
        :class="{'hide-bar': !show_bar, 'seek-bar-tall': tallBar}"
        @mouseenter="seek_hover = true"
        @mouseleave="seek_hover = false"
        @mousemove="onSeekHover"
        @click="onSeekClick"
      >
        <!-- Hover timestamp tooltip -->
        <div 
          v-if="seek_hover" 
          class="seek-tooltip"
          :style="{ left: seek_tooltip_left + 'px' }"
        >
          {{ seek_tooltip_time }}
        </div>
        <!-- Progress (played portion) -->
        <div class="seek-bar-progress" :style="{ width: seek_progress + '%' }"></div>
        <!-- Playback position thumb -->
        <div class="seek-bar-thumb" :class="{'seek-bar-thumb-dragging': is_dragging}" :style="{ left: seek_progress + '%' }" @mousedown.prevent="onThumbDragStart"></div>
        <!-- Hover indicator -->
        <div v-if="seek_hover" class="seek-bar-hover" :style="{ width: seek_hover_percent + '%' }"></div>
        <!-- Hover position marker -->
        <div v-if="seek_hover" class="seek-bar-hover-marker" :style="{ left: seek_hover_percent + '%' }"></div>
      </div>

      <div class="control-bar" :class="{'hide-bar': !show_bar, 'control-bar-seek': !is_live, 'control-bar-tall': tallBar}">
        <k-btn icon style="height: 24px;" @click="play_toggle"><k-icon size="l">{{play_icon}}</k-icon></k-btn>
        <!-- Current time / Duration display -->
        <div class="seek-time-display" :style="{ visibility: duration > 0 ? 'visible' : 'hidden' }">
          {{ formatTime(currentTime) }} / {{ formatTime(duration) }}
        </div>
        <div class="seek-time-display-placeholder">
          <template v-if="back_to_live">
            <k-btn style="height: 24px !important; margin-left: 4px; margin-top: 2px" size="xs" flat no-margin @click="seek_back_to_live">
              <k-icon small color="red">circle</k-icon><span class="live-text">&nbsp;Live</span>
            </k-btn>
          </template>
        </div>

        <k-btn icon style="height: 24px;" @click="refresh"><k-icon size="l">refresh</k-icon></k-btn>
        <k-btn icon style="height: 24px;" @click="mute_toggle"><k-icon size="l">{{volume_icon}}</k-icon></k-btn>

        <div :style="{width: (fullscreen ? '120px' : '60px')}" style="margin-right: 8px; margin-left: 8px; height: 22px;">
          <k-slider 
            v-model="current_volume" 
            hide-details 
            style="margin-top: -2px;" 
            color="var(--ks-col-primary)" 
            :min="0.0" 
            :max="1.0" 
            :step="0.0001">
          </k-slider>
        </div>
        <k-btn icon style="height: 24px;" @click="fullscreen_toggle"><k-icon size="l">{{fullscreen_icon}}</k-icon></k-btn>
      </div>
    </div>
  </k-responsive>
</template>
<script>
  import screenfull from '@/lib/screenfull.js'
  import Hls from 'hls.js'
  import { lufsSupported, createLufsMeter, ShortTermLoudness } from '@/lib/lufs_meter.js'
  import { drawLufsRadar, RADAR_SLOTS } from '@/lib/lufs_radar.js'
  import { DiamondScope } from '@/lib/diamond_scope.js'
  import { createPeakMeter, AudioMeters } from '@/lib/audio_meter.js'
  import { FrameMonitor } from '@/lib/frame_monitor.js'
  import { createKeyframeStartLoader } from '@/lib/hls_keyframe_start.js'
  import { ExtraAudioTracks, extraAudioTracksSupported } from '@/lib/ts_audio_tracks.js'
  import { MisbLatency } from '@/lib/misb_timestamp.js'
  import { SeiTimecodes, formatTimecode } from '@/lib/sei_timecode.js'
  export default {
    name: "KVideoPlayer",
    props: {
      "load-player": {
        type: Boolean,
        default: true
      }, 
      "url": String, 
      "autoplay": {
        type: Boolean,
        default: false
      },
      // Starts playback by itself as soon as the video is found to be available
      "auto-start": {
        type: Boolean,
        default: false
      },
      "position": Number,
      "hide-bar": {
        type: Boolean,
        default: true
      },
      volume: {
        type: Number,
        default: 0.0001
      },
      "pause-on-refresh": {
        type: Boolean,
        default: false
      },
      "tall-bar": {
        type: Boolean,
        default: true
      },
      "frame-accurate-seek": {
        type: Boolean,
        default: false
      },
      // Shows the short-term loudness (EBU R128, LUFS) in the top right corner.
      // Measured before the player volume; when off nothing extra is created
      "lufs": {
        type: Boolean,
        default: false
      },
      // Shows a diamond display (RGB gamut) of the video over the picture; when off nothing extra is done
      "diamond": {
        type: Boolean,
        default: false
      },
      // Counts freeze and black frames while playing and shows them in the top right corner.
      // Every event is reported with "frame-event" ({id, type: "freeze" | "black", start, end} in epoch ms,
      // end is null while it goes on). When off nothing extra is done
      "frame-monitor": {
        type: Boolean,
        default: false
      },
      // Listening to "lufs-close" / "frame-monitor-close" shows a small close button next to the
      // LUFS reading / the freeze and black frame counts; the parent turns the prop off
      // Opens the loudness radar (needs `lufs`); clicking the LUFS reading also toggles it and
      // reports the new state with "lufs-radar-change"
      "lufs-radar": {
        type: Boolean,
        default: false
      },
      // Target loudness (LUFS) for the loudness radar, 0 LU on its scale
      "lufs-target": {
        type: Number,
        default: -23
      },
      // Peak level bars (dBFS) of every audio channel on the left side of the picture.
      // Measured before the player volume; when off nothing extra is created
      "audio-meters": {
        type: Boolean,
        default: false
      },
      // With `audio-meters`: also shows the MPEG-TS audio tracks that aren't played (decoded with
      // WebCodecs only for the meters). When off only the played track is shown and nothing extra is done
      "all-audio-tracks": {
        type: Boolean,
        default: false
      },
      // Shows the latency (local clock - capture time) from MISB ST 0604 timestamps in H.264 / H.265
      // SEI messages in the bottom right corner. Uses the local clock, so it has to be in time.
      // Listening to "latency-close" shows a close button. When off nothing extra is done
      "latency": {
        type: Boolean,
        default: false
      }
    },
    data () {
      return {
        video: undefined,
        hls: undefined,
        started: false,
        playing: false,
        muted: false,
        current_volume: this.volume,
        fullscreen: false,
        show_bar: true,
        currentTime: 0,
        duration: 0,
        seek_hover: false,
        seek_hover_percent: 0,
        seek_tooltip_left: 0,
        seek_tooltip_time: '0:00',
        clip_end: undefined,
        resume_time: undefined,
        resume_playing: undefined,
        pending_time: undefined,
        buffering: false,
        first_frame_loaded: false,
        is_dragging: false,
        was_playing_before_drag: false,
        _onDragMove: null,
        _onDragEnd: null,
        is_live: false,
        back_to_live: false,
        checking: false,
        // Loudness radar over the video, toggled by clicking the LUFS reading
        lufs_radar: this.lufsRadar,
        // Width of the audio meters; changes only with the channel count or player height
        audio_meters_width: 0,
        // Latency breakdown tooltip; updated only while the latency box is hovered
        latency_info: "Waiting for timestamps",
        unavailable: false
      }
    },
    watch: {
      volume(newVal) {
        this.current_volume = newVal;
      },
      current_volume(newVal) {
        this.apply_volume();
      },
      lufs(newVal) {
        if (newVal) this.setup_lufs();
        else this.stop_lufs_meter();
      },
      audioMeters(newVal) {
        if (newVal) this.$nextTick(() => this.setup_audio_meters());
        else this.stop_audio_meters();
      },
      allAudioTracks(newVal) {
        if (newVal) this.setup_extra_audio();
        else this.stop_extra_audio();
      },
      latency(newVal) {
        if (newVal) this.$nextTick(() => this.start_latency());
        else this.stop_latency();
      },
      frameMonitor(newVal) {
        if (newVal) this.$nextTick(() => this.start_frame_monitor());
        else this.stop_frame_monitor();
      },
      lufsRadar(newVal) {
        if (newVal !== this.lufs_radar) this.set_lufs_radar(newVal);
      },
      diamond(newVal) {
        if (newVal) this.$nextTick(() => this.start_diamond());
        else this.stop_diamond();
      },
      autoStart(newVal) {
        if (newVal) this.start();
      },
      loadPlayer(newVal) {
        if (newVal && !this.started) this.check_availability();
      },
      url() {
        this.unavailable = false;
        if (!this.started) {
          this.check_availability();
          return;
        }
        this.switch_source();
      },
      "position": function(new_position) {
        if (new_position > -1) {
          if (typeof(this.pending_time) != "undefined") {
            this.pending_time = new_position;
            return;
          }
          this.video.currentTime = new_position;
        }
      }
    },
    computed: {
      play_icon() {
        if(this.playing) return "pause";
        return "play_arrow";
      },
      volume_icon() {
        if(this.muted || this.current_volume == 0) return "volume_off";
        if(this.current_volume < 0.5) return "volume_down";
        return "volume_up";
      },
      fullscreen_icon() {
        if(this.fullscreen) return "fullscreen_exit";
        return "fullscreen";
      },
      seek_progress() {
        if (this.duration <= 0) return 0;
        return (this.currentTime / this.duration) * 100;
      }
    },
    methods: {
      play_clip(start, stop) {
        this.video.currentTime = start;
        this.video.play();
        this.clip_end = stop;
      },


      step(change) {
        if (!this.video) return;
        // Seeking before data is buffered makes hls.js adopt that time as its start position
        if (typeof(this.pending_time) != "undefined") {
          this.pending_time = Math.max(0, this.pending_time + change);
          return;
        }
        if (!this.started) return;
        var time = this.video.currentTime + change;
        if (this.duration > 0) time = Math.min(time, this.duration);
        this.video.currentTime = Math.max(0, time);
      },
      start_frame_tracking() {
        var video = this.video;
        this._frame_samples = [];
        this._frame_media_time = undefined;
        this._frame_presented = undefined;
        if (!this.frameAccurateSeek) return;
        if (!video || !video.requestVideoFrameCallback) return;
        var on_frame = (now, meta) => {
          if (video !== this.video) return;
          if (typeof(this._frame_media_time) != "undefined") {
            var frames = meta.presentedFrames - this._frame_presented;
            var delta = meta.mediaTime - this._frame_media_time;
            // Ignore seeks and long gaps; dropped frames are divided out
            if (frames > 0 && delta > 0 && delta / frames < 0.1) {
              this._frame_samples.push(delta / frames);
              if (this._frame_samples.length > 30) this._frame_samples.shift();
            }
          }
          this._frame_media_time = meta.mediaTime;
          this._frame_presented = meta.presentedFrames;
          video.requestVideoFrameCallback(on_frame);
        };
        video.requestVideoFrameCallback(on_frame);
      },
      frame_duration() {
        if (!this.frameAccurateSeek) return this._manifest_fps > 0 ? 1 / this._manifest_fps : 1 / 50;
        var samples = this._frame_samples || [];
        if (samples.length >= 5) {
          var sorted = samples.slice().sort((a, b) => a - b);
          return sorted[Math.floor(sorted.length / 2)];
        }
        if (this._manifest_fps > 0) return 1 / this._manifest_fps;
        if (samples.length > 0) return Math.min.apply(null, samples);
        return 1 / 50;
      },
      step_frame(count) {
        if (!this.video || !this.started || typeof(this.pending_time) != "undefined") return;
        if (this.playing) this.video.pause();
        var fd = this.frame_duration();
        var base = typeof(this._frame_media_time) != "undefined" ? this._frame_media_time : this.video.currentTime;
        // Aim at the middle of the target frame so rounding cannot land on a neighbour
        var time = base + count * fd + fd / 2;
        if (this.duration > 0) time = Math.min(time, this.duration);
        this.video.currentTime = Math.max(0, time);
      },
      fullscreenchanged() {
        if (screenfull.isFullscreen) {
          this.fullscreen = true;
        } else {
          this.fullscreen = false;
        }
      },
      fullscreen_toggle() {
        if(!this.video) return;
        var player = this.$refs.player_container
        if (screenfull.isEnabled) {
          screenfull.toggle(player);
        }
      },
      play_toggle() {
        if(!this.video) return;
        if(this.playing) {
          this.video.pause();
        }
        if(!this.playing) {
          this.video.play();
        }
      },
      refresh() {
        if (this._refreshing) return;
        this._refreshing = true;
        
        // currentTime lags behind pending seeks, so read the element itself
        var time = this.started && this.video ? this.video.currentTime : this.currentTime;
        if (typeof(this.pending_time) != "undefined") time = this.pending_time;
        if (!this.is_live && time > 0) {
          this.resume_time = time;
          this.resume_playing = this.playing;
        }
        if (!this.is_live) this.pending_time = time;
        if (this.pauseOnRefresh) this.resume_playing = false;
        
        this.playing = false;
        if (this.hls) {
          this.hls.destroy();
          this.hls = undefined;
        }
        
        // Stop and reset video without removing it from DOM
        if (this.video) {
          this.video.pause();
          this.video.src = '';
          this.video.load();
        }
        
        this.$nextTick(async () => {
          try {
            await this.check_availability();
            if (!this.unavailable && this.started) {
              this.play();
            }
          } finally {
            this._refreshing = false;
          }
        });
      },
      switch_source() {
        this.playing = false;
        this.resume_time = undefined;
        this.resume_playing = undefined;
        this.pending_time = undefined;
        this.clip_end = undefined;
        this.is_live = false;
        this.back_to_live = false;
        this.currentTime = 0;
        this.duration = 0;
        if (this.hls) {
          this.hls.destroy();
          this.hls = undefined;
        }
        if (this.video) {
          this.video.pause();
          this.video.src = '';
          this.video.load();
        }
        if (!this.url || !this.loadPlayer) return;
        // A user-selected source should start playing instead of holding the first frame
        this._force_play = true;
        this.play();
      },
      formatTime(seconds) {
        if (!isFinite(seconds) || seconds < 0) return '00:00';
        var h = Math.floor(seconds / 3600);
        var m = Math.floor((seconds % 3600) / 60);
        var s = Math.floor(seconds % 60);
        var sStr = s < 10 ? '0' + s : '' + s;
        if (h > 0) {
          var mStr = m < 10 ? '0' + m : '' + m;
          return h.toString().padStart(2, '0') + ':' + mStr + ':' + sStr;
        }
        return m + ':' + sStr;
      },
      mediaErrorCodeToName(code) {
        if (code === 1) return 'MEDIA_ERR_ABORTED';
        if (code === 2) return 'MEDIA_ERR_NETWORK';
        if (code === 3) return 'MEDIA_ERR_DECODE';
        if (code === 4) return 'MEDIA_ERR_SRC_NOT_SUPPORTED';
        return 'UNKNOWN_MEDIA_ERROR';
      },
      logVideoError(event, extra) {
        var mediaError = this.video && this.video.error ? this.video.error : null;
        var details = {
          eventType: event && event.type ? event.type : 'unknown',
          src: this.video ? (this.video.currentSrc || this.video.src || null) : null,
          networkState: this.video ? this.video.networkState : null,
          readyState: this.video ? this.video.readyState : null,
          browserMessage: event && event.message ? event.message : null,
          mediaError: mediaError ? {
            code: mediaError.code,
            codeName: this.mediaErrorCodeToName(mediaError.code),
            message: typeof mediaError.message === 'string' ? mediaError.message : null
          } : null
        };

        if (extra) {
          details.extra = extra;
        }

        console.error('[KVideoPlayer] Video playback error', details);
      },
      onSeekHover(event) {
        var rect = event.currentTarget.getBoundingClientRect();
        var x = event.clientX - rect.left;
        var percent = (x / rect.width) * 100;
        percent = Math.max(0, Math.min(100, percent));
        this.seek_hover_percent = percent;
        var time = (percent / 100) * this.duration;
        this.seek_tooltip_time = this.formatTime(time);
        // Keep tooltip within bounds
        var tooltipWidth = 52;
        var left = x - (tooltipWidth / 2);
        left = Math.max(0, Math.min(rect.width - tooltipWidth, left));
        this.seek_tooltip_left = left;
      },
      onSeekClick(event) {
        if (this.is_dragging) return;
        if (!this.video || this.duration <= 0) return;
        var rect = event.currentTarget.getBoundingClientRect();
        var x = event.clientX - rect.left;
        var percent = x / rect.width;
        percent = Math.max(0, Math.min(1, percent));
        this.video.currentTime = percent * this.duration;
        this.currentTime = percent * this.duration;
      },
      onThumbDragStart(event) {
        if (!this.video || this.duration <= 0) return;
        this.is_dragging = true;
        this.was_playing_before_drag = this.playing;
        this.video.pause();
        var self = this;
        this._onDragMove = function(e) {
          var container = self.$el.querySelector('.seek-bar-container');
          if (!container) return;
          var rect = container.getBoundingClientRect();
          var x = e.clientX - rect.left;
          var percent = Math.max(0, Math.min(1, x / rect.width));
          self.video.currentTime = percent * self.duration;
          self.currentTime = percent * self.duration;
          // Update tooltip
          self.seek_hover = true;
          self.seek_hover_percent = percent * 100;
          self.seek_tooltip_time = self.formatTime(percent * self.duration);
          var tooltipWidth = 52;
          var left = x - (tooltipWidth / 2);
          left = Math.max(0, Math.min(rect.width - tooltipWidth, left));
          self.seek_tooltip_left = left;
        };
        this._onDragEnd = function() {
          self.is_dragging = false;
          self.seek_hover = false;
          if (self.was_playing_before_drag) {
            self.video.play();
          }
          document.removeEventListener('mousemove', self._onDragMove);
          document.removeEventListener('mouseup', self._onDragEnd);
        };
        document.addEventListener('mousemove', this._onDragMove);
        document.addEventListener('mouseup', this._onDragEnd);
      },
      mute_toggle() {
        if(!this.video) return;
        if (this.audio_routed()) {
          this.muted = !this.muted;
          this.apply_volume();
          return;
        }
        this.video.muted = !this.video.muted;
        this.muted = this.video.muted;
      },
      // True when the element's audio goes through Web Audio (LUFS has been on for this element)
      audio_routed() {
        return !!(this._audio && this.video && this._audio.el === this.video);
      },
      // With Web Audio the element stays at full volume, so the meter before the gain node sees the
      // unchanged signal; the player volume and mute are applied by the gain node
      apply_volume() {
        if (!this.video) return;
        if (this.audio_routed()) {
          this.video.volume = 1;
          this.video.muted = false;
          this._audio.gain.gain.value = this.muted ? 0 : this.current_volume;
        } else {
          this.video.volume = this.current_volume;
        }
      },
      // Routes the element through Web Audio: source -> gain (player volume) -> speakers, source -> meters.
      // A media element can't be detached from Web Audio, so turning a meter off only stops that meter.
      ensure_audio() {
        var a = this._audio;
        if (!a) {
          var AudioContextClass = window.AudioContext || window.webkitAudioContext;
          var ctx = new AudioContextClass();
          a = this._audio = {ctx: ctx, el: null, source: null, gain: ctx.createGain(), meter: null, loudness: null, peak: null};
          a.gain.connect(ctx.destination);
          // Autoplay policy may keep the context suspended until the user interacts with the page
          a.resume = () => { if (a.ctx.state === "suspended") a.ctx.resume(); };
          document.addEventListener("pointerdown", a.resume, true);
        }
        if (a.el !== this.video) {
          if (a.source) a.source.disconnect();
          a.el = this.video;
          a.source = a.ctx.createMediaElementSource(this.video);
          a.source.connect(a.gain);
          if (a.meter) a.source.connect(a.meter);
          if (a.peak) a.source.connect(a.peak);
          this.video.addEventListener("play", a.resume);
        }
        this.apply_volume();
        a.resume();
        return a;
      },
      async setup_lufs() {
        if (!this.lufs || !this.video || !lufsSupported()) return;
        var a = this.ensure_audio();
        if (a.meter || a.meter_loading) return;
        a.meter_loading = true;
        try {
          var meter = await createLufsMeter(a.ctx);
          // Destroyed or turned off while the module was loading
          if (this._audio !== a || !this.lufs) {
            meter.disconnect();
            return;
          }
          a.meter = meter;
          a.loudness = new ShortTermLoudness();
          // Short-term history for the radar, kept also while it is hidden
          a.history = new Float32Array(RADAR_SLOTS).fill(NaN);
          a.history_pos = 0;
          meter.port.onmessage = (e) => {
            if (!a.loudness) return;
            a.loudness.add(e.data.sums, e.data.count);
            var short_term = a.loudness.value();
            a.history[a.history_pos] = short_term;
            a.history_pos = (a.history_pos + 1) % RADAR_SLOTS;
            this.show_lufs(short_term);
            if (this.lufs_radar) this.draw_lufs_radar();
          };
          a.source.connect(meter);
          // The meter outputs silence; connecting it keeps it processing in every browser
          meter.connect(a.ctx.destination);
        } catch (err) {
          console.error("[KVideoPlayer] LUFS meter failed", err);
        } finally {
          a.meter_loading = false;
        }
      },
      stop_lufs_meter() {
        var a = this._audio;
        if (!a || !a.meter) return;
        a.meter.port.onmessage = null;
        a.meter.disconnect();
        if (a.source) {
          try {
            a.source.disconnect(a.meter);
          } catch (err) {
            // Not connected
          }
        }
        a.meter = null;
        a.loudness = null;
        a.history = null;
        if (this.lufs_radar) this.set_lufs_radar(false);
      },
      // Peak bars are drawn when the worklet reports (~30 times per second)
      async setup_audio_meters() {
        if (!this.audioMeters || !this.video || !this.$refs.audio_meters || !lufsSupported()) return;
        var a = this.ensure_audio();
        if (a.peak || a.peak_loading) return;
        a.peak_loading = true;
        try {
          var node = await createPeakMeter(a.ctx);
          if (this._audio !== a || !this.audioMeters || !this.$refs.audio_meters) {
            node.disconnect();
            return;
          }
          a.peak = node;
          a.peak_meters = new AudioMeters(this.$refs.audio_meters);
          this.setup_extra_audio();
          node.port.onmessage = (e) => {
            var meters = a.peak_meters;
            if (!meters) return;
            meters.add(e.data, performance.now(), this.extra_audio_peaks());
            var width = meters.draw();
            if (width !== this.audio_meters_width) this.audio_meters_width = width;
          };
          a.source.connect(node);
          node.connect(a.ctx.destination);
        } catch (err) {
          console.error("[KVideoPlayer] audio meters failed", err);
        } finally {
          a.peak_loading = false;
        }
      },
      stop_audio_meters() {
        this.audio_meters_width = 0;
        this.stop_extra_audio();
        var a = this._audio;
        if (!a || !a.peak) return;
        a.peak.port.onmessage = null;
        a.peak.disconnect();
        if (a.source) {
          try {
            a.source.disconnect(a.peak);
          } catch (err) {
            // Not connected
          }
        }
        a.peak = null;
        a.peak_meters = null;
      },
      // Extra audio tracks for the meters; only while both props are on
      setup_extra_audio() {
        if (!this.allAudioTracks || !this.audioMeters || this._extra_audio || !extraAudioTracksSupported()) return;
        var extra = this._extra_audio = new ExtraAudioTracks();
        _.each(this._init_pts, (v, cc) => extra.set_init_pts(cc, v.base, v.timescale));
        this._extra_from = undefined;
      },
      stop_extra_audio() {
        if (!this._extra_audio) return;
        this._extra_audio.reset();
        this._extra_audio = null;
      },
      // Peaks of the extra tracks since the previous call, following the playback position
      extra_audio_peaks() {
        var extra = this._extra_audio;
        if (!extra || !this.video) return null;
        var t = this.video.currentTime;
        var from = this._extra_from;
        // First call or a seek: just the latest moment
        if (from === undefined || from > t || t - from > 1) from = t - 0.05;
        this._extra_from = t;
        return extra.peaks(from, t);
      },
      // Called by the fragment loader with the raw data of every fragment
      on_hls_fragment(data, frag) {
        if (!frag || frag.type !== "main") return;
        if (this._extra_audio) this._extra_audio.push(data, frag.cc);
        if (this._latency) this._latency.timecodes.push(data, frag.cc);
      },
      // Timestamps come from hls.js (FRAG_PARSING_USERDATA), SMPTE timecodes from the fragment loader;
      // the texts are updated 10 times per second
      start_latency() {
        if (!this.latency || this._latency || !this.video) return;
        var l = this._latency = {meter: new MisbLatency(), timecodes: new SeiTimecodes(), since: performance.now(), timer: null, hover: false};
        this.latency_info = "Waiting for timestamps";
        l.timer = setInterval(() => this.show_latency(), 100);
        this.show_latency();
      },
      stop_latency() {
        var l = this._latency;
        if (!l) return;
        clearInterval(l.timer);
        this._latency = null;
      },
      // Written directly to the element: a reactive value would re-render the player on every update
      show_latency() {
        var l = this._latency;
        var el = this.$refs.latency_display;
        if (!l || !el || !this.video) return;
        var ms = l.meter.latency(this.video.currentTime, Date.now());
        var text = "";
        if (ms !== null) text = "Latency " + (ms / 1000).toFixed(ms < 10000 && ms > -10000 ? 2 : 1) + " s";
        // Nothing found after a while of playing
        else if (performance.now() - l.since > 8000 && !this.video.paused) text = "No timestamp";
        if (el.textContent !== text) el.textContent = text;
        var tc_el = this.$refs.timecode_display;
        if (tc_el) {
          var offsets = _.mapValues(this._init_pts, v => v.base / v.timescale);
          var tc = l.timecodes.timecode(this.video.currentTime, offsets);
          var tc_text = tc ? "TC " + formatTimecode(tc) : "";
          if (tc_el.textContent !== tc_text) tc_el.textContent = tc_text;
        }
        // Tooltip twice per second while hovered
        if (l.hover && (l.ticks = (l.ticks || 0) + 1) % 5 === 1) this.update_latency_info();
      },
      set_latency_hover(on) {
        if (!this._latency) return;
        this._latency.hover = on;
        this._latency.ticks = 0;
        if (on) this.update_latency_info();
      },
      // Splits the latency of the shown frame: waiting for the segment to be complete, delivery of the
      // segment (encoding, packaging, upload, CDN, playlist refresh, download) and the player buffer
      update_latency_info() {
        var l = this._latency;
        if (!l || !this.video) return;
        var b = l.meter.breakdown(this.video.currentTime, Date.now());
        var s = (ms) => (ms / 1000).toFixed(2) + " s";
        var text;
        if (!b) {
          text = "Waiting for timestamps";
        } else if (b.segment === undefined) {
          text = "Total: " + s(b.total);
        } else {
          text = "HLS: " + s(b.segment + b.buffer) +
            "<br>&nbsp;&nbsp;Segment build: " + s(b.segment) +
            "<br>&nbsp;&nbsp;Player buffer: " + s(b.buffer) +
            "<br>Delivery: " + s(b.delivery) +
            "<br>&nbsp;&nbsp;(encoding, upload, CDN, playlist refresh, download)" +
            (b.segment_length !== null ? "<br>&nbsp;&nbsp;Segment length: " + s(b.segment_length) +
              "<br>&nbsp;&nbsp;Delivery without segment length: " + s(b.delivery - b.segment_length) : "") +
            "<br>Total: " + s(b.total) +
            "<br><i>Delivery and total include any difference<br>between the encoder's and this computer's clock</i>";
        }
        if (text !== this.latency_info) this.latency_info = text;
      },
      // Diamond display: analyses at most ~15 frames per second and only new frames
      // (requestVideoFrameCallback), so a paused video costs nothing
      start_diamond() {
        if (!this.diamond || this._diamond || !this.video || !this.$refs.diamond) return;
        var scope = this._diamond = {scope: new DiamondScope(this.$refs.diamond), last: 0, el: null, id: null, raf: null};
        var tick = (now) => {
          if (this._diamond !== scope) return;
          var video = this.video;
          now = typeof(now) === "number" ? now : performance.now();
          if (video && video.readyState >= 2 && now - scope.last >= 66) {
            scope.last = now;
            scope.scope.draw(video);
          }
          schedule();
        };
        var schedule = () => {
          var video = this.video;
          if (video && video.requestVideoFrameCallback) {
            scope.el = video;
            scope.id = video.requestVideoFrameCallback(tick);
          } else {
            scope.raf = requestAnimationFrame(tick);
          }
        };
        // Draw the current frame (also when paused), then follow new frames
        scope.scope.draw(this.video);
        schedule();
      },
      stop_diamond() {
        var s = this._diamond;
        if (!s) return;
        this._diamond = null;
        if (s.el && s.id !== null && s.el.cancelVideoFrameCallback) s.el.cancelVideoFrameCallback(s.id);
        if (s.raf !== null) cancelAnimationFrame(s.raf);
      },
      // Checks the shown frame 5 times per second while playing. A timer (not frame callbacks) is used
      // so a stalled stream that delivers no new frames is detected as a freeze too.
      start_frame_monitor() {
        if (!this.frameMonitor || this._frame_monitor || !this.video) return;
        // Detector times are performance.now() values; reported as wall clock times
        var epoch = (t) => Date.now() - (performance.now() - t);
        var on_event = (e) => {
          this.$emit("frame-event", {id: e.type + ":" + e.since, type: e.type, start: epoch(e.since), end: e.end === null ? null : epoch(e.end)});
        };
        var fm = this._frame_monitor = {monitor: new FrameMonitor(on_event), timer: null, active: null};
        fm.timer = setInterval(() => {
          var video = this.video;
          var now = performance.now();
          if (!video || video.paused || video.ended || video.readyState < 2) fm.monitor.pause(now);
          else fm.monitor.check(video, now);
          this.show_frame_monitor();
        }, 200);
        this.$nextTick(() => this.show_frame_monitor());
      },
      stop_frame_monitor() {
        var fm = this._frame_monitor;
        if (!fm) return;
        clearInterval(fm.timer);
        // Ends an event going on so it gets its end time
        fm.monitor.pause(performance.now());
        this._frame_monitor = null;
      },
      frame_display_click() {
        if (this.$listeners["frame-monitor-click"]) this.$emit("frame-monitor-click");
        else this.reset_frame_monitor();
      },
      reset_frame_monitor() {
        if (!this._frame_monitor) return;
        this._frame_monitor.monitor.reset(performance.now());
        this.show_frame_monitor();
      },
      // Written directly to the element: a reactive value would re-render the player on every check
      show_frame_monitor() {
        var fm = this._frame_monitor;
        var el = this.$refs.frame_display;
        if (!fm || !el) return;
        var text = fm.monitor.text(performance.now());
        if (el.textContent !== text) el.textContent = text;
        var active = fm.monitor.active();
        if (active !== fm.active) {
          fm.active = active;
          if (this.$refs.frame_item) this.$refs.frame_item.classList.toggle("frame-display-active", active);
        }
      },
      toggle_lufs_radar() {
        this.set_lufs_radar(!this.lufs_radar);
      },
      set_lufs_radar(value) {
        this.lufs_radar = value;
        this._radar_reserve = null;
        if (value) this.$nextTick(() => this.draw_lufs_radar());
        this.$emit("lufs-radar-change", value);
      },
      draw_lufs_radar() {
        var canvas = this.$refs.lufs_radar;
        var a = this._audio;
        if (!canvas || !a || !a.history) return;
        // The radar keeps clear of the status texts in the top right corner. The reserved width only grows
        // while the radar is open, so it doesn't jump when the texts get shorter again.
        var status = this.$refs.player_status;
        var r = this._radar_reserve || {width: 0, height: 0};
        if (status && status.offsetWidth) {
          r = {width: Math.max(r.width, status.offsetWidth + 8), height: Math.max(r.height, status.offsetTop + status.offsetHeight + 4)};
        }
        this._radar_reserve = r;
        drawLufsRadar(canvas, a.history, a.history_pos, a.loudness.value(4), this.lufsTarget, r);
      },
      // Written directly to the element: a reactive value would re-render the player 10 times per second
      show_lufs(value) {
        var el = this.$refs.lufs_display;
        if (!el) return;
        var text = isFinite(value) && value > -70 ? value.toFixed(1) + " LUFS" : "-\u221e LUFS";
        if (el.textContent !== text) el.textContent = text;
      },
      seek_back_to_live() {
        if (!this.is_live) return;
        if (!this.hls) return;
        this.video.currentTime = this.hls.liveSyncPosition;
        this.back_to_live = false;
      },
      async check_url() {
        if (!this.url) return false;
        var controller = new AbortController();
        try {
          var response = await fetch(this.url, { method: 'GET', signal: controller.signal });
          return response.ok;
        } catch (e) {
          return false;
        } finally {
          controller.abort();
        }
      },
      async check_availability() {
        if (!this.url || !this.loadPlayer) return;
        var token = this._check_token = (this._check_token || 0) + 1;
        this.checking = true;
        this.unavailable = false;
        var available = await this.check_url();
        if (token !== this._check_token) return;
        this.checking = false;
        this.unavailable = !available;
        if (available && this.autoStart && !this.started) this.play();
      },
      // Starts playback unless it is already running or loading; an unavailable video is checked again first
      start() {
        if (!this.loadPlayer || !this.url || this.started || this.checking) return;
        if (this.unavailable) this.retry();
        else this.play();
      },
      async retry() {
        await this.check_availability();
        if (!this.unavailable && !this.started) this.play();
      },
      set_buffering(on) {
        if (!on) {
          clearTimeout(this._buffering_timer);
          this._buffering_timer = undefined;
          this.buffering = false;
          return;
        }
        if (this.buffering || this._buffering_timer) return;
        this._buffering_timer = setTimeout(() => {
          this._buffering_timer = undefined;
          this.buffering = true;
        }, 500);
      },
      play() {
        var self = this;
        this.started = true;
        this.$nextTick(() => {
          var autoplay = this.autoplay;
          this.video = this.$refs.player;
          if (this.lufs) this.setup_lufs();
          if (this.audioMeters) this.setup_audio_meters();
          if (this.diamond) this.start_diamond();
          if (this.frameMonitor) this.start_frame_monitor();
          if (this.latency) this.start_latency();
          this.set_buffering(true);
          var video_el = this.video;
          var buffering_handler = (value) => () => { if (this.video === video_el) this.set_buffering(value); };
          ['loadstart', 'waiting', 'seeking'].forEach(name => video_el.addEventListener(name, buffering_handler(true)));
          ['loadeddata', 'canplay', 'playing', 'seeked', 'error'].forEach(name => video_el.addEventListener(name, buffering_handler(false)));
          this._manifest_fps = undefined;
          this.start_frame_tracking();
          if (Hls.isSupported()) {
            // Safari's ManagedMediaSource can evict buffers and break seeking.
            // fLoader: starts TS streams from the first fragment with video (see hls_keyframe_start.js)
            var hls_config = { debug: false, preferManagedMediaSource: false, fLoader: createKeyframeStartLoader(Hls, (data, frag) => this.on_hls_fragment(data, frag)) };
            if (typeof(this.resume_time) != "undefined") {
              hls_config.startPosition = this.resume_time;
            }
            this.hls = new Hls(hls_config);
            this.hls.loadSource(this.url);
            this.hls.attachMedia(this.video);
            this.hls.on(Hls.Events.MEDIA_ATTACHED, () => {
              this.apply_volume();
            });
            var start_decided = false;
            this.hls.on(Hls.Events.LEVEL_LOADED, (event, data) => {
              const details = data.details;
              if (details) {
                self.is_live = details.live;
              }
              if (!start_decided) {
                start_decided = true;
                // On the very first VOD load stay paused so only the first frame is shown
                var hold_first_frame = !self.is_live && !self.first_frame_loaded && !self._force_play;
                if (!hold_first_frame && self.resume_playing !== false) self.video.play();
                self._force_play = false;
                self.resume_time = undefined;
                self.resume_playing = undefined;
              }
              var level = self.hls && self.hls.levels ? self.hls.levels[data.level] : undefined;
              var fps = level && level.attrs ? parseFloat(level.attrs['FRAME-RATE']) : NaN;
              if (fps > 0) self._manifest_fps = fps;
            });
            this.hls.on(Hls.Events.ERROR, (event, data) => {
              this.logVideoError(event, {
                source: 'hls.js',
                type: data ? data.type : null,
                details: data ? data.details : null,
                fatal: data ? data.fatal : null,
                reason: data && data.reason ? data.reason : null,
                response: data && data.response ? data.response : null
              });
            });
            // Timestamp base per discontinuity, maps the extra audio tracks to the playback position
            this._init_pts = {};
            if (this._extra_audio) this._extra_audio.reset();
            // Media time starts again with a new hls.js instance
            if (this._latency) {
              this._latency.meter.reset();
              this._latency.timecodes.reset();
            }
            this.hls.on(Hls.Events.FRAG_PARSING_USERDATA, (event, data) => {
              if (!this._latency || data.id !== "main") return;
              // Arrival of the fragment as wall clock time
              var end = data.frag && data.frag.stats && data.frag.stats.loading ? data.frag.stats.loading.end : 0;
              var arrival = end ? Date.now() - (performance.now() - end) : Date.now();
              this._latency.meter.add_userdata(data.samples, data.frag ? {sn: data.frag.sn, arrival: arrival, duration: data.frag.duration} : null);
            });
            this.hls.on(Hls.Events.INIT_PTS_FOUND, (event, data) => {
              if (data.id !== "main") return;
              this._init_pts[data.frag.cc] = {base: data.initPTS, timescale: data.timescale};
              if (this._extra_audio) this._extra_audio.set_init_pts(data.frag.cc, data.initPTS, data.timescale);
            });
            this.video.addEventListener("volumechange", (event) => {
              // With Web Audio the element is kept at full volume and current_volume is the player volume
              if (this.audio_routed()) return;
              this.current_volume = this.video.volume;
            });
            this.video.addEventListener("play", (event) => {
              this.playing = true;
              if (this.hideBar) {
                this.show_bar = false;
              }

            })
            this.video.addEventListener("pause", (event) => {
              this.playing = false;
              if (this.hideBar) {
                this.show_bar = true;
              }
            })
            this.video.addEventListener("error", (event) => {
              this.logVideoError(event);
            });
            this.video.addEventListener("playing", (event) => {
              this.$emit("input", {
                width: this.video.videoWidth,
                height: this.video.videoHeight
              })
            })
            var video = this.video;
            this.video.addEventListener("timeupdate", () => {
              if (video !== this.video) return;
              this.currentTime = this.video.currentTime;
              this.duration = this.video.duration || 0;

              if (this.is_live) {
                if (this.currentTime < this.hls.liveSyncPosition - 3.0) {
                  this.back_to_live = true;
                }
              }
              this.$emit("position-duration", {
                position: this.currentTime,
                duration: this.duration
              });
              if (typeof(this.clip_end) != "undefined") {
                if (this.currentTime > this.clip_end) {
                  this.video.pause();
                  this.clip_end = undefined;
                  this.$emit("playing-clip", false);
                }
              }
            })
            this.video.addEventListener("loadedmetadata", () => {
              this.duration = this.video.duration || 0;
            })
            this.video.addEventListener("loadeddata", () => {
              if (video !== this.video) return;
              var target = this.pending_time;
              this.pending_time = undefined;
              if (typeof(target) != "undefined" && Math.abs(this.video.currentTime - target) > 0.5) {
                this.video.currentTime = target;
              }
              if (!this.is_live && this.first_frame_loaded == false) {
                this.first_frame_loaded = true;
                this.$emit("loaded");
              }
            })
          }
          else if (this.video.canPlayType('application/vnd.apple.mpegurl')) {
            this.video.src = this.url;
            var resume_playing = this.resume_playing;
            this.resume_time = undefined;
            this.resume_playing = undefined;
            this.video.addEventListener('canplay', () => {
              if (resume_playing !== false) this.video.play();
            });
          }
        })

      }
    },
    mounted() {
      this.check_availability();
      if (screenfull.isEnabled) {
        screenfull.on('change', this.fullscreenchanged);
      }
    },
    beforeDestroy() {
      clearTimeout(this._buffering_timer);
      this.stop_diamond();
      this.stop_frame_monitor();
      this.stop_extra_audio();
      this.stop_latency();
      if(this.hls) this.hls.destroy();
      if (this._audio) {
        this.stop_lufs_meter();
        this.stop_audio_meters();
        document.removeEventListener("pointerdown", this._audio.resume, true);
        this._audio.ctx.close();
        this._audio = null;
      }
      if (this._onDragMove) document.removeEventListener('mousemove', this._onDragMove);
      if (this._onDragEnd) document.removeEventListener('mouseup', this._onDragEnd);
    }
  }
</script>
<style scoped>
/* Own stacking context: the internal layers below only order player parts */
.ks-video-player {
  isolation: isolate;
}
.start-container {
  width: 100%; 
  height: 100%; 
  display: flex; 
  align-items: center; 
  justify-content: center; 
  background: var(--ks-background-color-dark);
}


.unavailable-container {
  color: rgba(255, 255, 255, 0.85);
  /* Lets the content adapt to small players (e.g. minimum size videos) */
  container-type: size;
}
.unavailable-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  box-sizing: border-box;
  max-width: 100%;
  padding: 0 8px;
  text-align: center;
}
.unavailable-text {
  font-size: 14px;
}
/* Too low for the text above the button: put them side by side and make them smaller */
@container (max-height: 120px) {
  .unavailable-content {
    flex-direction: row;
  }
  .unavailable-text {
    font-size: 12px;
  }
  /* x-large sets the size inline */
  .unavailable-content .ks-icon {
    font-size: 24px !important;
  }
}

.buffering-indicator {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 1;
  display: flex;
  padding: 4px;
  border-radius: 50%;
  background: var(--ks-background-color-op60);
  pointer-events: none;
}

.player-status {
  position: absolute;
  top: 4px;
  right: 4px;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 3px;
  max-width: calc(100% - 8px);
  pointer-events: none;
}
.player-status-item {
  display: flex;
  align-items: center;
  gap: 0.3em;
  padding: 0.1em 0.45em;
  border-radius: 0.25em;
  background: var(--ks-background-color-op60);
  color: rgba(255, 255, 255, 0.9);
  font-variant-numeric: tabular-nums;
  line-height: 1.4;
  white-space: nowrap;
  pointer-events: auto;
}
.player-status-text {
  cursor: pointer;
}
/* Hidden until there is something to show (the latency box shows a spinner instead) */
.player-status-item:not(.latency-display):has(> .player-status-text:empty) {
  display: none;
}
/* Small close button that turns the measurement off */
.player-status-close {
  display: inline-flex;
  margin-right: -0.15em;
  opacity: 0.6;
  cursor: pointer;
}
.player-status-close:hover {
  opacity: 1;
}
.player-status-close .ks-icon {
  font-size: 0.85em;
}
/* Grow a little with the player: LUFS 14px up to a 500px wide player, then 2.8% of the width (max 26px);
   frame counts 11px up to 610px, then 1.8% (max 16px) */
.lufs-display {
  font-size: clamp(14px, 2.8cqw, 26px);
}
/* Room for the usual readings (e.g. -25.0 LUFS) so the box doesn't change size with the value.
   Roboto has equal width digits (with tabular-nums), so the value doesn't jitter either. */
.lufs-display .player-status-text {
  font-family: Roboto, sans-serif;
  min-width: 5.8em;
  text-align: right;
}
.frame-display {
  font-size: clamp(11px, 1.8cqw, 16px);
}
/* Stays above the control bar (32px) so it doesn't move when the bar appears */
.player-bottom-status {
  position: absolute;
  right: 4px;
  bottom: 36px;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 3px;
  max-width: calc(100% - 8px);
  pointer-events: none;
}
.latency-display,
.timecode-display {
  font-size: clamp(11px, 1.8cqw, 16px);
}
.latency-display .player-status-text,
.timecode-display .player-status-text {
  font-family: Roboto, sans-serif;
}
.latency-pending {
  display: inline-flex;
  align-items: center;
  gap: 0.4em;
}
.latency-display .player-status-text:empty {
  display: none;
}
/* The value has arrived */
.latency-display:has(> .player-status-text:not(:empty)) .latency-pending {
  display: none;
}
/* A freeze or black frame is going on */
.frame-display-active {
  background: rgba(200, 40, 40, 0.85);
  color: #fff;
}
.player-overlay-canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  pointer-events: none;
}
/* Peak meters on the left; the width is set by the drawing code */
.audio-meters {
  position: absolute;
  top: 4px;
  left: 1px;
  height: calc(100% - 8px);
  width: 0;
  z-index: 2;
  pointer-events: none;
}
.lufs-radar {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  opacity: 0.8;
  pointer-events: none;
}

.hide-bar {
  transition: opacity 0.2s ease-out;
  opacity: 0;
}
.player-container {
  width: 100%; 
  height: 100%; 
  display: flex;
  background: var(--ks-background-color-dark);
}
/* Lets the status texts scale with the player width; only when they are shown */
.player-container-scaled {
  container-type: inline-size;
}

.player-container:hover .hide-bar {
  opacity: 1;
}
.control-bar {
  background-color: var(--ks-background-color-op60); 
  position: absolute; 
  height: 28px; 
  bottom: 0px; 
  left: 0px; 
  width: 100%; 
  z-index: 1;
  display: grid; 
  grid-template-columns: 28px minmax(0, 1fr) 28px 28px max-content 34px;
}
.control-bar-tall {
  box-sizing: border-box;
  height: 32px;
  padding-top: 4px;
}
.seek-bar-container.seek-bar-tall {
  bottom: 22px;
}
.control-bar .seek-time-display {
  display: none;
}
.control-bar .seek-time-display-placeholder {
  min-width: 0;
  overflow: hidden;
  container-type: inline-size;
}
@container (max-width: 70px) {
  .live-text {
    display: none;
  }
}


.control-bar-seek {
  grid-template-columns: 28px minmax(0, 1fr) 28px 28px max-content 34px;
}
.control-bar-seek .seek-time-display {
  display: flex;
  min-width: 0;
  overflow: hidden;
  font-family: Roboto;
}
.control-bar-seek .seek-time-display-placeholder {
  display: none;
}


.seek-bar-container {
  position: absolute;
  bottom: 18px;
  left: 0;
  width: 100%;
  height: 23px;
  background: transparent;
  z-index: 2;
  cursor: pointer;
}
.seek-bar-container::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 0;
  width: 100%;
  height: 3px;
  transform: translateY(-50%);
  background: var(--ks-col-info);
  pointer-events: none;
  z-index: 0;
}
.seek-bar-progress {
  position: absolute;
  top: 50%;
  left: 0;
  height: 3px;
  transform: translateY(-50%);
  background: var(--ks-col-warning-alternative);
  pointer-events: none;
  z-index: 2;
}
.seek-bar-thumb {
  position: absolute;
  top: 50%;
  width: 12px;
  height: 12px;
  background: var(--ks-col-warning-alternative);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  pointer-events: auto;
  z-index: 3;
  box-shadow: 0 0 3px rgba(0, 0, 0, 0.4);
  cursor: pointer;
}
.seek-bar-thumb:active,
.seek-bar-thumb-dragging {
  cursor: grabbing;
  transform: translate(-50%, -50%) scale(1.4);
}
.seek-bar-container:hover .seek-bar-thumb {
  transform: translate(-50%, -50%) scale(1.3);
}
.seek-bar-hover {
  position: absolute;
  top: 50%;
  left: 0;
  height: 3px;
  transform: translateY(-50%);
  pointer-events: none;
  background: var(--ks-col-warning);
  z-index: 1;
}
.seek-bar-hover-marker {
  position: absolute;
  top: 50%;
  width: 4px;
  height: 8px;
  transform: translate(-50%, -50%);
  background: #fff;
  border-radius: 2px;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.8);
  pointer-events: none;
  z-index: 4;
}
.seek-tooltip {
  position: absolute;
  bottom: 100%;
  margin-bottom: 6px;
  background: rgba(0, 0, 0, 0.8);
  color: #fff;
  font-size: 12px;
  padding: 3px 6px;
  border-radius: 3px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 5;
  font-variant-numeric: tabular-nums;
}
.seek-time-display {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.85);
  padding: 0 8px;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
</style>
<style>
  /* Safari syntax */
  :-webkit-full-screen {
    background-color: black;
  }

  /* IE11 */
  :-ms-fullscreen {
    background-color: black;
  }

  /* Standard syntax */
  :fullscreen {
    background-color: black;
  }
</style>